<script>
import { computed, defineComponent, watch } from '@vue/composition-api';
import { format } from 'date-fns';
import ptbr from 'date-fns/locale/pt';
import flattenDeep from 'lodash.flattendeep';
import { useCampaignsResults } from '../../composables/useCampaignsResults';
import { useCampaignsStats } from '../../composables/useCampaignStats';
import { useManagerDashboard } from '../../composables/useManagerDashboard';
import Loader from '../Loader.vue';
import DashboardData from './DashboardData.vue';
import DashboardGraph from './DashboardGraph.vue';

export default defineComponent({
  components: {
    Loader,
    DashboardData,
    DashboardGraph,
  },
  props: {
    storeSelected: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { root }) {
    const {
      load,
      campaignsZipperRevenue: allCampaignsZipperRevenue,
      campaignsRevenue: allCampaignsRevenue,
      clientsContacted: allClientsContacted,
      loading: loadingCampaignsResults,
    } = useCampaignsResults();
    const { load: loadStats, loading: loadingStats, stats, error: errorStats } = useCampaignsStats();
    const { currentRevenue, storeSelected, formatNumber } = useManagerDashboard();

    const formatDate = (d) => format(d, 'MMMM', {
      locale: ptbr,
    });

    const customer = computed(() => root.$store.state.customer);
    const currentMonth = computed(() => formatDate(currentRevenue.value.date));

    watch(
      storeSelected,
      async () => {
        if (storeSelected.value) {
          await load(customer.value.id, { storeIds: storeSelected.value.id });
        }
      },
      { immediate: true },
    );

    watch(
      currentRevenue,
      async () => {
        await loadStats(
          storeSelected.value.id,
          customer.value.id,
          currentRevenue.value.date.getFullYear(),
          currentRevenue.value.date.getMonth(),
        );
      },
      { immediate: true },
    );

    const totalRevenueThisMonth = computed(() => {
      if (allCampaignsRevenue.value) {
        const found = allCampaignsRevenue.value.find(
          (el) => el.date.getMonth() === currentRevenue.value.date.getMonth()
            && el.date.getFullYear() === currentRevenue.value.date.getFullYear(),
        );
        if (found) return found;
      }
      return { total: 0 };
    });

    const sentPercentage = computed(() => {
      if (stats.value && stats.value.length > 0) {
        const flattedStats = flattenDeep(stats.value);
        const result = flattedStats.reduce((sum, val) => ({
          sent: sum.sent + val.sent,
          total: sum.total + val.total,
        }));
        return ((result.sent / result.total) * 100).toFixed(0);
      }
      return 0;
    });

    // eslint-disable-next-line max-len
    const clientsContacted = computed(() => {
      if (allClientsContacted.value) {
        // eslint-disable-next-line max-len
        const found = allClientsContacted.value.find(
          (el) => el.date.getMonth() === currentRevenue.value.date.getMonth()
            && el.date.getFullYear() === currentRevenue.value.date.getFullYear(),
        );
        if (found) return found;
      }
      return {};
    });

    return {
      formatNumber,
      totalRevenueThisMonth,
      loadingStats,
      currentRevenue,
      sentPercentage,
      currentMonth,
      allCampaignsZipperRevenue,
      clientsContacted,
      loadingCampaignsResults,
      errorStats
    };
  },
});
</script>

<template>
  <div v-if="!loadingCampaignsResults">
    <DashboardData
      :icon="'$vuetify.icons.crmLogoIcon'"
      :subtitle="`de vendas influenciadas pela CRM360 em ${currentMonth}`"
      :pre-subtitle="
        `${formatNumber((currentRevenue.total / totalRevenueThisMonth.total) * 100)}%`
      "
      :title="`R$ ${formatNumber(currentRevenue.total)}`"
      is-main
    />
    <DashboardGraph
      v-if="!loadingCampaignsResults"
      :data="allCampaignsZipperRevenue"
    />
    <Loader v-else />
    <DashboardData
      :icon="'$vuetify.icons.shoppingBagIcon'"
      :subtitle="'de vendas identificadas em '"
      :pos-subtitle="currentMonth"
      :title="`R$ ${formatNumber(totalRevenueThisMonth.total)}`"
      :tooltip-text="'Considerando clientes identificados (com CPF informado no momento do faturamento)'"
    />
    <DashboardData
      :icon="'fas fa-paper-plane'"
      :loading="loadingStats"
      :subtitle="!loadingStats && !errorStats.load ? 'de envios em ' : ''"
      :pos-subtitle="!loadingStats && !errorStats.load? currentMonth : ''"
      :title="!loadingStats && !errorStats.load ? `${formatNumber(sentPercentage)}%` : 'Erro ao carregar os dados'"
      :small-caption="!loadingStats && errorStats.load ? 'Volte em instantes para obter os resultados' : ''"
    />
    <DashboardData
      :icon="'$vuetify.icons.userPlusIcon'"
      :subtitle="'clientes únicos contatados em'"
      :pos-subtitle="currentMonth"
      :title="`${formatNumber(clientsContacted.total)}`"
    />
    <v-divider class="mb-3" />
  </div>
</template>
